import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Loader2, Tv, Maximize2, DollarSign, Clock, PieChart, Crosshair, Ruler, Radio
} from 'lucide-react'
import useGlobalCustomerStore from '../../../stores/globalCustomerStore'
import broadcastService, { ExposureBrand, PublishedBroadcast } from '../../../services/broadcastService'
import KPICard from '../../ui/KPICard'
import BroadcastUnavailable from './BroadcastUnavailable'
import { SponsorLogo, rollup } from './BroadcastSponsorCards'
import { colourMap } from './BroadcastValueDonuts'
import LocationSplitBar from './LocationSplitBar'
import { SeasonRow, SponsorSeasonChart, SponsorShareOfVoice, SponsorBroadcastTable } from './SponsorSeason'
import { formatEventDate } from './broadcastCustomerFormat'
import { secs, pct } from '../broadcastReviewConstants'
import { rateFor, valueOf, formatMoney, formatAreaSeconds } from '../broadcastValuation'

/** This sponsor's counted brand rows on one broadcast. */
interface Delivery {
  broadcast: PublishedBroadcast
  brands: ExposureBrand[]
}

/**
 * One sponsor across every published broadcast: what the season delivered.
 *
 * TWO SOURCES, EACH FOR WHAT ONLY IT HAS. Area, value, broadcast count and
 * share come from the library's sponsor_area slices — the same rollup the
 * Overview card and donut read, so clicking a card never lands on a different
 * number. On-screen time and logo size come from each broadcast's exposure
 * payload, filtered to the brand rows that resolve to this partner; the
 * library list does not carry them.
 *
 * One exposure request per broadcast the sponsor appeared in. A library is
 * tens of broadcasts, so that is fine for now; a sponsor endpoint in CRO
 * replaces it when the season chart and proof gallery need more than this.
 */
const BroadcastSponsorPage: React.FC = () => {
  const { partnerId } = useParams<{ partnerId: string }>()
  const navigate = useNavigate()
  const { selectedCustomer } = useGlobalCustomerStore()
  const customerId = selectedCustomer ? String(selectedCustomer.id) : ''

  const [library, setLibrary] = useState<PublishedBroadcast[]>([])
  const [deliveries, setDeliveries] = useState<Delivery[]>([])
  const [loading, setLoading] = useState(true)
  const [locked, setLocked] = useState(false)

  useEffect(() => {
    if (!customerId || !partnerId) return
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const lib = (await broadcastService.listBroadcasts(customerId)).data ?? []
        const mine = lib.filter((b) =>
          (b.sponsor_area ?? []).some((s) => s.key === partnerId && Number(s.area_seconds) > 0))
        const payloads = await Promise.all(mine.map((b) => broadcastService.getBroadcast(customerId, b.id)))
        if (cancelled) return
        setLibrary(lib)
        setDeliveries(payloads.map((p) => ({
          broadcast: p.data.broadcast,
          // counts, not just partner_id: an excluded or own-brand row is
          // not delivery, and the library rollup leaves it out too.
          brands: p.data.brands.filter((x) => x.partner_id === partnerId && x.counts)
        })))
      } catch (e) {
        if (!cancelled) setLocked((e as { status?: number })?.status === 403)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [customerId, partnerId])

  const all = useMemo(() => rollup(library), [library])
  const sponsor = all.find((s) => s.id === partnerId) ?? null

  // Share is taken over the same measure the card shows: value when this
  // sponsor has any, otherwise area. Mixing the two would compare a priced
  // numerator against an unpriced denominator.
  const share = useMemo(() => {
    if (!sponsor) return null
    if (sponsor.value !== null) {
      const total = all.reduce((n, s) => n + (s.value ?? 0), 0)
      return total > 0 ? sponsor.value / total : null
    }
    const total = all.reduce((n, s) => n + s.area, 0)
    return total > 0 ? sponsor.area / total : null
  }, [all, sponsor])

  const shape = useMemo(() => {
    const rows = deliveries.flatMap((d) => d.brands)
    const ms = rows.reduce((n, b) => n + Number(b.total_duration_ms ?? 0), 0)
    const area = rows.reduce((n, b) => n + Number(b.total_area_seconds ?? 0), 0)
    const peaks = rows.map((b) => b.max_area_share).filter((v): v is number => v !== null)
    return {
      ms,
      peak: peaks.length ? Math.max(...peaks.map(Number)) : null,
      // Duration-weighted, as on the brand page: a two-minute hold counts
      // for more than a half-second glimpse.
      avg: ms > 0 ? area / (ms / 1000) : null
    }
  }, [deliveries])

  // One row per broadcast. Area and value from the library slice, so the
  // bars and the table sum to the KPI cards; time, peak and the link from
  // the exposure rows, which only the per-broadcast payload carries.
  const season = useMemo<SeasonRow[]>(() => library.flatMap((b) => {
    const area = Number((b.sponsor_area ?? []).find((x) => x.key === partnerId)?.area_seconds ?? 0)
    if (area <= 0) return []
    const d = deliveries.find((x) => x.broadcast.id === b.id)
    const brands = d?.brands ?? []
    const peaks = brands.map((x) => x.max_area_share).filter((v): v is number => v !== null)
    const top = [...brands].sort((x, y) => Number(y.total_area_seconds) - Number(x.total_area_seconds))[0]
    return [{
      broadcast: b,
      area,
      value: valueOf(area, rateFor(b)).value,
      ms: d ? brands.reduce((n, x) => n + Number(x.total_duration_ms ?? 0), 0) : null,
      peak: peaks.length ? Math.max(...peaks.map(Number)) : null,
      brandKey: top?.brand_key ?? null
    }]
  }), [library, deliveries, partnerId])

  // Where the value was earned: this sponsor's area by position, priced per
  // broadcast at that broadcast's rate. In value mode unpriced broadcasts are
  // left out, as they are from the Media value card the bar breaks down.
  const byZone = useMemo(() => {
    const priced = all.find((s) => s.id === partnerId)?.value != null
    const amounts = new Map<string, number>()
    const labels = new Map<string, string>()
    for (const b of library) {
      const rate = rateFor(b)
      if (priced && rate === null) continue
      for (const z of b.sponsor_zone_area ?? []) {
        if (z.partner_id !== partnerId) continue
        const a = Number(z.area_seconds ?? 0)
        amounts.set(z.key, (amounts.get(z.key) ?? 0) + (priced ? (valueOf(a, rate).value ?? 0) : a))
        if (z.label) labels.set(z.key, z.label)
      }
    }
    return { amounts, labels }
  }, [library, all, partnerId])

  const colour = useMemo(
    () => colourMap(library, (b) => b.sponsor_area).get(partnerId ?? '') ?? '#2a78d6',
    [library, partnerId]
  )

  const span = useMemo(() => {
    const dates = deliveries.map((d) => d.broadcast.event_date).filter((d): d is string => !!d).sort()
    if (dates.length === 0) return null
    const first = formatEventDate(dates[0]!)
    const last = formatEventDate(dates[dates.length - 1]!)
    return first === last ? first : `${first} – ${last}`
  }, [deliveries])

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-gray-500">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
    </div>
  }
  if (locked) return <BroadcastUnavailable locked />
  if (!sponsor) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
        <p className="text-sm font-medium text-gray-600">No measured exposure for this sponsor</p>
        <button onClick={() => navigate('/broadcasts')}
          className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-700">
          Back to overview
        </button>
      </div>
    )
  }

  const { equivalents } = valueOf(sponsor.area, null)
  const partlyPriced = sponsor.value !== null && sponsor.priced < sponsor.broadcasts
  // One switch for the whole page: value when this sponsor has any priced
  // broadcast, area-seconds otherwise — the same rule as the Media value card.
  const priced = sponsor.value !== null
  const fmt = (n: number) => priced ? formatMoney(n, sponsor.currency) : formatAreaSeconds(n)
  // The breadcrumb a broadcast opened from here carries back.
  const origin = { path: `/broadcasts/sponsors/${sponsor.id}`, label: sponsor.name }

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate('/broadcasts')}
          className="inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-800 mb-3"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to overview
        </button>

        <div className="flex items-center gap-4 mb-5">
          <SponsorLogo id={sponsor.id} name={sponsor.name} customerId={customerId} size="h-16 w-16" />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 truncate">{sponsor.name}</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Measured value delivered across {sponsor.broadcasts}{' '}
              {sponsor.broadcasts === 1 ? 'broadcast' : 'broadcasts'}
              {span && <> · {span}</>}
            </p>
          </div>
        </div>

        {/* Same cards, icons and colours as the per-broadcast brand page, so
            the season view reads as that page summed. Row one is the value
            calculation in order; row two is the shape of the delivery. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-2">
          <KPICard
            label="Broadcasts"
            value={sponsor.broadcasts}
            icon={Radio}
            colorScheme="slate"
            tooltip="Published broadcasts in which this sponsor had counted exposure."
          />
          <KPICard
            label="Area-seconds"
            value={formatAreaSeconds(sponsor.area)}
            icon={Maximize2}
            colorScheme="blue"
            tooltip="Screen area multiplied by time, in full-screen-seconds, summed across every broadcast."
          />
          <KPICard
            label=":30 Equivalents"
            value={equivalents.toFixed(2)}
            icon={Tv}
            colorScheme="indigo"
            tooltip="Area-seconds expressed as full-screen 30-second spots, summed across every broadcast."
          />
          <KPICard
            label="Media value"
            value={sponsor.value === null ? '—' : formatMoney(sponsor.value, sponsor.currency)}
            icon={DollarSign}
            colorScheme="emerald"
            tooltip={partlyPriced
              ? `Priced from ${sponsor.priced} of ${sponsor.broadcasts} broadcasts; the others have no audience recorded yet, so their exposure is not in this figure.`
              : 'Each broadcast\'s area-seconds priced at its own 30-second rate, then summed. A dash means none of these broadcasts is priced yet.'}
          />
          <KPICard
            label="On screen"
            value={secs(shape.ms)}
            icon={Clock}
            colorScheme="slate"
            tooltip="Total time visible, summed across every appearance on every broadcast. Overlapping appearances in different placements each count."
          />
          <KPICard
            label={sponsor.value !== null ? 'Share of sponsor value' : 'Share of sponsor exposure'}
            value={pct(share)}
            icon={PieChart}
            colorScheme="purple"
            tooltip={sponsor.value !== null
              ? 'This sponsor\'s media value as a share of all sponsors\' media value across the library.'
              : 'Nothing is priced yet, so this is this sponsor\'s share of all sponsored area-seconds instead of value.'}
          />
          <KPICard
            label="Peak logo size"
            value={pct(shape.peak)}
            icon={Crosshair}
            colorScheme="purple"
            tooltip="The largest share of the screen this sponsor filled at any single moment across the season."
          />
          <KPICard
            label="Average logo size"
            value={pct(shape.avg)}
            icon={Ruler}
            colorScheme="blue"
            tooltip="Average share of the screen filled while on screen, weighted by how long each appearance lasted, across every broadcast."
          />
        </div>
        {partlyPriced && (
          <p className="text-[11px] text-gray-400">
            Media value covers {sponsor.priced} of {sponsor.broadcasts} broadcasts; the rest are not priced yet.
          </p>
        )}
        <div className="mb-3" />

        <LocationSplitBar
          title={priced ? 'Media value by placement' : 'Area-seconds by placement'}
          amounts={byZone.amounts}
          labelOf={(code) => byZone.labels.get(code) ?? code}
          format={fmt}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
          <div className="lg:col-span-2">
            <SponsorSeasonChart rows={season} priced={priced} currency={sponsor.currency} color={colour} origin={origin} />
          </div>
          <SponsorShareOfVoice sponsors={all} selfId={sponsor.id} priced={priced}
                               currency={sponsor.currency} color={colour} />
        </div>

        <SponsorBroadcastTable rows={season} currency={sponsor.currency} origin={origin} />
      </div>
    </div>
  )
}

export default BroadcastSponsorPage
